import type { IAccount } from "@/modules/account/common/types";
import { TransactionTypes, TransferKeys } from "@/modules/transaction/common/enum";
import type { ISaveAsTemplate } from "@/modules/transaction/common/types";
import { InsufficientFundsModal } from "@/modules/transaction/components";
import {
  useLazyGetTransactionNumberQuery,
  useSaveAccountTransferAsTemplateMutation,
  useSaveBudgetTransferAsTemplateMutation,
  useSaveCardTransferAsTemplateMutation,
  useSaveCurrencyExchangeAsTemplateMutation,
  useSaveLoanRepaymentAsTemplateMutation,
  useSaveOwnAccountTransferAsTemplateMutation,
  useSignTransferMutation,
} from "@/modules/transaction/services";
import { AccessManagementDialogContent, LOGIN_OTP_COUNT } from "@/shared/common/constants";
import {
  AccessManagementKeys,
  ActionType,
  AppPaths,
  CloudflareAsset,
  ErrorKeys
} from "@/shared/common/enums";
import type { ICommission, IObject, TInsufficientFundsModal } from "@/shared/common/types";
import {
  AccessManagementDialog,
  BusinessLayout,
  CloudFlareImage,
  Commission,
  OTPPopup,
} from "@/shared/components";
import { cleanupAmount } from "@/shared/helpers";
import { useNotify } from "@/shared/hooks";
import { setActiveTransactionId } from "@/shared/state/common";
import { debounce } from "@/shared/utils";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import uuid4 from "uuid4";

interface Props<T> {
  backUrl?: string,
  title?: string,
  getCommission?: (p: IObject) => Promise<{ data?: ICommission }>,
  children: ReactElement,
  getTransferId: (values: T) => Promise<string>,
  canGetCommission?: boolean,
  fieldChanges?: IObject;
  page: string;
  isEdit?: boolean;
  isLoading: boolean;
  isSaveDisabled?: boolean;
  isSignDisabled?: boolean;
  isGetTransferLoading?: boolean;
  accountsList: IAccount[];
  withCommission?: boolean;
  hasChanges?: boolean;
  className?: string;
  transactionType: string;
  canClose?: boolean;
}

export const TransferLayout = <_, T>(
  {
    page,
    title,
    canClose,
    children,
    isLoading,
    isSaveDisabled,
    isSignDisabled,
    isGetTransferLoading,
    getTransferId,
    getCommission,
    transactionType,
    fieldChanges,
    canGetCommission,
    accountsList,
    withCommission = true,
    isEdit = false,
    hasChanges = false,
    className,
  }: Props<T>,
) => {
  const navigate = useNavigate();
  const idempotencyKey = useRef(uuid4()).current;
  const { t } = useTranslation();
  const {
    showErrorAlertNotification,
    showWarningAlertNotification,
    showSuccessSnackbarNotification,
  } = useNotify();
  const { setValue, setError, watch, trigger, isSubmitting } = useFormProps();
  const dispatch = useDispatch();

  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const [isAccessManagementOpen, setIsAccessManagementOpen] = useState<boolean>(false);
  const [isModalOpen, setToggleModal] = useState<boolean>(false);
  const [savedTransferId, setSavedTransferId] = useState<string | null>(null);
  const [insufficientModal, setInsufficientModal] = useState<TInsufficientFundsModal>(null);

  const [signTransfer] = useSignTransferMutation();
  const [getTransactionNumber] = useLazyGetTransactionNumberQuery();
  const [saveAccountTransferAsTemplate] = useSaveAccountTransferAsTemplateMutation();
  const [saveOwnAccountTransferAsTemplate] = useSaveOwnAccountTransferAsTemplateMutation();
  const [saveCardTransferAsTemplate] = useSaveCardTransferAsTemplateMutation();
  const [saveBudgetTransferAsTemplate] = useSaveBudgetTransferAsTemplateMutation();
  const [saveCurrencyExchangeAsTemplate] = useSaveCurrencyExchangeAsTemplateMutation();
  const [saveLoanRepaymentAsTemplate] = useSaveLoanRepaymentAsTemplateMutation();

  const values = watch?.() || {};

  const saveAsTemplateApi: Partial<Record<TransactionTypes, (args: ISaveAsTemplate) => unknown>> = {
    [TransactionTypes.AccountTransfer]: saveAccountTransferAsTemplate,
    [TransactionTypes.OwnAccountTransfer]: saveOwnAccountTransferAsTemplate,
    [TransactionTypes.CardTransfer]: saveCardTransferAsTemplate,
    [TransactionTypes.BudgetTransfer]: saveBudgetTransferAsTemplate,
    [TransactionTypes.CurrencyExchange]: saveCurrencyExchangeAsTemplate,
    [TransactionTypes.LoanRepayment]: saveLoanRepaymentAsTemplate,
  };

  const handleSaveAndGetTransferId = async (isSave?: boolean): Promise<string> => {
    return getTransferId(values as T)
      .then((id) => {
        const isSaveAsTemplate = values.isSaveAsTemplate;
        const templateName = values.templateName;
        if (isSaveAsTemplate && templateName) {
          saveAsTemplateApi[transactionType as TransactionTypes]?.({
            id,
            name: templateName,
            idempotencyKey,
          });
        }
        return id;
      })
      .catch((error) => {
        const errors = error?.data?.errors;
        const warning = error?.data?.warnings?.[0];

        if (warning === ErrorKeys.TransactionRestrictedByStopTool) {
          setIsAccessManagementOpen(true);
        } else if (warning) {
          showWarningAlertNotification(t(warning));
          if (isSave) {
            setToggleModal(false);
            document.getElementById("form-container")?.scrollTo({
              top: 0, behavior: "smooth"
            });
          }
        } else if (errors) {
          Object.keys(errors).forEach((key) => {
            setError?.(key, { message: errors[key] });
            setToggleModal(false);
          });
        } else {
          showErrorAlertNotification(error);
          document.getElementById("form-container")?.scrollTo({ top: 0, behavior: "smooth" });
        }
        return Promise.reject(error);
      }).finally(() => {
        dispatch(setActiveTransactionId(null));
      });
  };

  const handleGetTransferId = async () => {
    if (savedTransferId) {
      return savedTransferId;
    } else {
      const id = await handleSaveAndGetTransferId();
      setSavedTransferId(id);
      setIsSaved(true);
      return id;
    }
  };

  const handleChangeToken = (token: string) => {
    if (token.length === LOGIN_OTP_COUNT) {
      handleGetTransferId().then((id) => {
        setIsSaved(true);
        signTransfer({
          id,
          otp: token,
          transactionType: transactionType,
        }).unwrap().then(() => {
          showSuccessSnackbarNotification(t("alerts.transferHasBeenSuccessfullySigned"));
          setTimeout(() => navigate(AppPaths.TransactionHistory));
        }).catch((error) => {
          showErrorAlertNotification(error);
        });
      });
    }
  };

  const handleSaveTransfer = () => {
    handleSaveAndGetTransferId(true).then(() => {
      setIsSaved(true);
      showSuccessSnackbarNotification(t("alerts.transferHasBeenSuccessfullySaved"));
      setTimeout(() => navigate(AppPaths.TransactionHistory));
    }).finally(() => {
      setToggleModal(false);
      setInsufficientModal(null);
    });
  };

  const handleCheckFunds = (actionType: TInsufficientFundsModal) => {
    trigger?.().then((isValid) => {
      if (isValid) {
        const debitAccount = accountsList?.find(
          (account: IAccount) => account.number === values.debitAccount,
        );
        if (debitAccount && debitAccount?.expectedBalance < +cleanupAmount(values.amount)) {
          setInsufficientModal(actionType);
        } else if (actionType === ActionType.Sign) {
          setIsOtpModalOpen(true);
        } else {
          setToggleModal(true);
        }
      }
    });
  };

  const cancelTransactionEdit = () => {
    navigate(-1);
  };

  const handleConfirmInsufficientFunds = () => {
    if (insufficientModal === ActionType.Sign) {
      setInsufficientModal(null);
      setIsOtpModalOpen(true);
    } else {
      handleSaveTransfer();
    }
  };

  const handleGetCommission = useMemo(() =>
    debounce((fieldChanges: IObject) => {
      getCommission?.(fieldChanges).then(({ data }) => {
        setValue(TransferKeys.CommissionData, data || 0);
      });
    }, 700), []);

  useEffect(() => {
    if (canGetCommission && withCommission) {
      handleGetCommission(fieldChanges as IObject);
    }
  }, [fieldChanges]);

  useEffect(() => {
    if (!isEdit) {
      getTransactionNumber()
        .unwrap()
        .then((transactionNumber) => {
          setValue(TransferKeys.TransactionNumber, transactionNumber);
        });
    }
  }, []);

  return (
    <BusinessLayout
      title={title}
      isEdit={isEdit}
      isSaved={isSaved}
      canClose={canClose}
      className={className}
      hasChanges={hasChanges}
    >
      <>
        <OTPPopup
          togglePopup={setIsOtpModalOpen}
          isOpen={isOtpModalOpen}
          isLoading={isLoading}
          handleChangeToken={handleChangeToken}
        />
        <Modal
          isOpen={isModalOpen}
          onClose={() => setToggleModal(false)}
          onSubmit={handleSaveTransfer}
          titleProps={{
            title: t("transfer.saveModal.title"),
            size: "large"
          }}
          subtitle={t("transfer.saveModal.description")}
          closeIcon={!isLoading}
          closeOnOutsideClick={!isLoading}
          buttonProps={{
            cancel: {
              type: "tertiary",
              buttonText: t("general.cancel"),
              buttonActionType: "button",
              onClick: () => setToggleModal(false),
              disabled: isLoading,
            },
            confirm: {
              type: "primary",
              buttonText: t("general.save"),
              buttonActionType: "submit",
              isLoading: isLoading,
              disabled: isLoading,
            },
          }}
        />
        {isGetTransferLoading ? (
          <div className="flexbox align-items--center" style={{ minHeight: "50vh" }}>
            <Spinner isFullScreen={false}/>
          </div>
        ) : children}
        {withCommission && (
          <div className="commission-container-wrapper mt-16">
            {isSaved && (
              <Tooltip
                id="save-transaction"
                position={Positions.BOTTOM_CENTER}
                text={t("transfer.alreadySaved")}
              />
            )}
            <Commission accountsList={accountsList}/>
          </div>
        )}
        {isEdit ? (
          <div
            className="full-width flexbox flex-direction--row justify-content--end form-actions">
            <Button
              className="mt-32 truncate-btn"
              buttonActionType="button"
              disabled={isSubmitting}
              type="secondary"
              onClick={cancelTransactionEdit}
              isLoading={isSubmitting}
              buttonText={t("general.cancel")}
              data-testid="cancel-transaction-edit"
              data-ga-id="cancel-transaction-edit"
              data-ga-action={`Cancel ${page} transaction edit`}
            />
            <Button
              className="mt-32 ml-16 submit-button truncate-btn"
              buttonActionType="button"
              buttonText={t("general.saveChanges")}
              data-testid="save-transaction-change"
              data-ga-id="save-transaction-changes"
              data-ga-action={`Save ${page} transaction changes`}
              disabled={isSubmitting || !hasChanges || isSaveDisabled}
              onClick={() => handleCheckFunds(ActionType.Save)}
            />
          </div>
        ) : (
          <div
            className="full-width flexbox flex-direction--row justify-content--end form-actions">
            {isSaved && !withCommission && (
              <Tooltip
                id="save-transaction"
                position={Positions.TOP_CENTER}
                text={t("transfer.alreadySaved")}
              />
            )}
            <Button
              className="mt-32 truncate-btn"
              buttonActionType="button"
              buttonText={t("general.save")}
              type="secondary"
              id="save-transaction"
              data-testid="save-transaction"
              data-ga-id="save-transaction"
              data-ga-action={`Save ${page} transaction`}
              disabled={isSubmitting || isSaveDisabled || isSaved}
              onClick={() => handleCheckFunds(ActionType.Save)}
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Save}/>
                ),
                alignment: "left",
              }}
            />
            <Button
              className="mt-32 ml-16 submit-button truncate-btn"
              buttonActionType="button"
              disabled={isSubmitting || isSignDisabled}
              onClick={() => handleCheckFunds(ActionType.Sign)}
              isLoading={isSubmitting}
              buttonText={t("general.sign")}
              data-testid="sign-transaction"
              data-ga-id="sign-transaction"
              data-ga-action={`Sign ${page} transaction`}
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage
                    {...props}
                    path={CloudflareAsset.SignLight}
                  />
                ),
                alignment: "left",
                type: "primary",
              }}
            />
          </div>
        )}
        <InsufficientFundsModal
          onConfirm={handleConfirmInsufficientFunds}
          onClose={() => setInsufficientModal(null)}
          isOpen={!!insufficientModal}
          isLoading={isLoading}
          buttonText={t(`general.${insufficientModal}`)}
        />
        <AccessManagementDialog
          isOpen={isAccessManagementOpen}
          onClose={() => setIsAccessManagementOpen(false)}
          content={AccessManagementDialogContent[
            AccessManagementKeys[transactionType as keyof typeof AccessManagementKeys]]
          }
        />
      </>
    </BusinessLayout>
  );
};
